import { useState } from "react";
import { PRINTERS } from "../config/printers";

/**
 * Modal for selecting rework type (Printing or Other) when starting a REWORK job for PA users
 * If "Printing" is selected, shows printer selection
 */
export default function ReworkTypeModal({ job, onSelect, onCancel }) {
  const [step, setStep] = useState("type"); // "type" or "printer"
  const [selectedPrinter, setSelectedPrinter] = useState(null);

  const handleTypeSelect = (type) => {
    if (type === "printing") {
      setStep("printer");
    } else {
      // "other" - proceed without printer
      onSelect({ type: "other", printer: null });
    }
  };

  const handlePrinterSubmit = () => {
    if (!selectedPrinter) return;
    onSelect({ type: "printing", printer: selectedPrinter });
  };

  const handleBack = () => {
    setStep("type");
    setSelectedPrinter(null);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative bg-white border border-gray-200 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-slate-100">
          <h2 className="text-lg font-semibold text-gray-900">
            {step === "type" ? "Rework Type" : "Select Printer"}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {step === "type"
              ? "What type of rework are you doing?"
              : "Choose which printer will be used"}
          </p>
        </div>

        {/* Job Info */}
        <div className="px-6 py-3 border-b border-gray-200 bg-slate-200">
          <p className="text-sm text-gray-700">
            <span className="text-gray-500">Job:</span>{" "}
            <span className="font-medium">{job?.jobNumber || job?.name || "Unknown"}</span>
          </p>
        </div>

        {/* Content */}
        {step === "type" ? (
          /* Type Selection */
          <div className="px-6 py-6">
            <div className="grid grid-cols-1 gap-4">
              <button
                onClick={() => handleTypeSelect("printing")}
                className="p-6 rounded-xl border-2 border-gray-300 bg-slate-100 hover:bg-blue-50 hover:border-blue-500 transition-all text-left group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-600/20 flex items-center justify-center group-hover:bg-blue-600/40 transition-colors">
                    <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Printing</h3>
                    <p className="text-sm text-gray-500">Reprinting on a printer</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleTypeSelect("other")}
                className="p-6 rounded-xl border-2 border-gray-300 bg-slate-100 hover:bg-purple-900/30 hover:border-purple-500 transition-all text-left group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-purple-600/20 flex items-center justify-center group-hover:bg-purple-600/40 transition-colors">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Other</h3>
                    <p className="text-sm text-gray-500">Non-printing rework tasks</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        ) : (
          /* Printer Selection */
          <div className="px-6 py-4 max-h-[50vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              {PRINTERS.map((printer) => (
                <button
                  key={printer.id}
                  onClick={() => setSelectedPrinter(printer)}
                  className={`
                    p-3 rounded-lg border text-left transition-all flex items-center gap-3
                    ${
                      selectedPrinter?.id === printer.id
                        ? "bg-blue-600 border-blue-500 text-gray-900"
                        : "bg-slate-100 border-gray-300 text-gray-800 hover:bg-slate-100 hover:border-gray-500"
                    }
                  `}
                >
                  {printer.icon && (
                    <img
                      src={`${import.meta.env.BASE_URL}printers/${printer.icon}.png`}
                      alt={printer.name}
                      className="w-10 h-10 rounded-full object-cover border-2 border-gray-300 flex-shrink-0"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="min-w-0">
                    <span className="text-sm font-medium block truncate">{printer.name}</span>
                    {printer.model && (
                      <span className={`text-xs ${selectedPrinter?.id === printer.id ? 'text-blue-200' : 'text-gray-500'}`}>
                        {printer.model}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-200 bg-slate-100 flex justify-between">
          {step === "printer" ? (
            <>
              <button
                onClick={handleBack}
                className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-800 text-sm font-medium transition-colors"
              >
                Back
              </button>
              <div className="flex gap-3">
                <button
                  onClick={onCancel}
                  className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-800 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePrinterSubmit}
                  disabled={!selectedPrinter}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium transition-colors
                    ${
                      selectedPrinter
                        ? "bg-blue-600 hover:bg-blue-700 text-gray-900"
                        : "bg-gray-600 text-gray-500 cursor-not-allowed"
                    }
                  `}
                >
                  Start Job
                </button>
              </div>
            </>
          ) : (
            <div className="flex justify-end w-full">
              <button
                onClick={onCancel}
                className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-800 text-sm font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
